import { Button, Tooltip, Typography } from 'antd'
import { MdOutlineFileOpen, MdOutlinePlayCircle } from 'react-icons/md'

import { notify } from '@/Ship/Helpers/notify'
import { parseApiErrorDescription } from '@/Ship/Helpers/parseApiError'

import { ReloadButton } from '../Components/ReloadButton'
import { ResponseCell } from '../Components/ResponseCell'
import {
    EXCHANGE_PROCESSABLE_STATUSES,
    EXCHANGE_STATUS_OPTIONS,
    EXCHANGE_TYPE_OPTIONS,
} from '../Contracts/ExchangeQueueOptions'
import { parseResponse } from '../Helpers/exchangePayload'
import { ProcessImportItemTask } from '../Tasks/ProcessImportItemTask'

import type { IResourceConfig } from '@/Core/Contracts/UI'

/** Ключ стану crudContext, у якому лежить запис для вікна «Запит» */
export const REQUEST_MODAL_STATE = 'erpRequestRecord'

/** Сирий запис (до трансформації комірок таблиці) */
const rawOf = (record: any): Record<string, any> => record?._original ?? record ?? {}

const openRequest = (crudContext: any, record: any) => {
    const raw = rawOf(record)
    if (!raw['request']) return
    crudContext?.setState?.(REQUEST_MODAL_STATE, raw)
}

const canProcess = (raw: Record<string, any>): boolean =>
    EXCHANGE_PROCESSABLE_STATUSES.includes(raw['status']) && raw['_permissions']?.['process_import_item'] !== false

/** Ручна обробка запису: повідомлення за результатом і перезавантаження списку */
const processImport = async (crudContext: any, raw: Record<string, any>) => {
    try {
        const result = await ProcessImportItemTask(raw['uuid'])
        if (result?.['status'] === 'error') {
            const reason = parseResponse(result?.['response'])
            notify.error('Помилка при обробці', reason ? { description: reason } : undefined)
        } else {
            notify.success('Запис оброблено')
        }
    } catch (error) {
        const description = await parseApiErrorDescription(error, '')
        notify.error('Не вдалося обробити запис', description ? { description } : undefined)
    }
    crudContext?.listContext?.actions?.load?.(true)
}

/**
 * Журнал обміну ERP: стандартна колекція ядра (картка, фільтр з чіпами, налаштування
 * колонок), лише для читання. Дії в рядку: перегляд запиту (вікно з JSON) і ручна
 * обробка записів у статусах «Очікує імпорту» / «Помилка».
 */
export const ErpExchangeResource: IResourceConfig = {
    key: 'Setting.ErpExchange',
    title: 'Журнал обміну ERP',
    restUrl: '/services/erp-exchange',
    pageUrl: '/settings/erp-exchange',
    fields: {
        id: {
            label: 'ID',
            type: 'number',
            /** У фільтрі: оператор (=, >, ≥, <, ≤) або діапазон від–до */
            isFilterRange: true,
        },
        type: {
            label: 'Тип операції',
            type: 'status',
            options: EXCHANGE_TYPE_OPTIONS,
            isFilterMultiple: true,
            /** Бейдж без меню зміни: тип задає обмін */
            inputProps: { disabled: true },
        },
        status: {
            label: 'Статус',
            type: 'status',
            options: EXCHANGE_STATUS_OPTIONS,
            isFilterMultiple: true,
            /** Бейдж без меню зміни: статус ставить обробник обміну */
            inputProps: { disabled: true },
        },
        entityType: {
            label: 'Тип сутності',
            type: 'string',
        },
        entityId: {
            label: 'ID сутності',
            type: 'number',
        },
        request: {
            label: 'Запит',
            type: 'string',
        },
        response: {
            label: 'Відповідь',
            type: 'string',
        },
        createdAt: {
            label: 'Дата створення',
            type: 'datetime',
        },
        updatedAt: {
            label: 'Дата оновлення',
            type: 'datetime',
        },
        uuid: {
            label: 'UUID',
            inputProps: { disabled: true },
        },
    },
    /** Найновіші записи зверху */
    list: {
        sorter: { key: 'id', order: 'descend' },
    },
    table: {
        showColumns: ['type', 'status', 'entityType', 'entityId', 'request', 'createdAt', 'updatedAt', 'response'],
        /** Запит і відповідь малюють власні комірки з сирого значення */
        dataSourceNoTransform: ['request', 'response'],
        scroll: { x: 'max-content' },
        eventHandlers: {
            onBuildColumns: {
                /** Подвійний клік по рядку відкриває запит замість картки запису */
                rowOpensRequest: ({ tableContext }: any) => {
                    const crudContext = tableContext?.storages?.crudContext
                    tableContext.props.onRow = (record: any) => ({
                        onDoubleClick: () => openRequest(crudContext, record),
                    })
                },
            },
            onBuildColumn: {
                customCells: ({ column, tableContext }: any) => {
                    const crudContext = tableContext?.storages?.crudContext
                    const widths: Record<string, number> = {
                        id: 60,
                        type: 120,
                        status: 140,
                        entityType: 150,
                        entityId: 90,
                        request: 64,
                        createdAt: 136,
                        updatedAt: 136,
                        response: 180,
                    }
                    if (widths[column.dataIndex]) column.width = widths[column.dataIndex]

                    if (column.dataIndex === 'request') {
                        column.align = 'center'
                        column.render = (raw: string | null, record: any) =>
                            raw ? (
                                <Tooltip title="Переглянути запит">
                                    <Button
                                        type="text"
                                        size="small"
                                        aria-label="Переглянути запит"
                                        icon={<MdOutlineFileOpen />}
                                        onClick={(event) => {
                                            event.stopPropagation()
                                            openRequest(crudContext, record)
                                        }}
                                    />
                                </Tooltip>
                            ) : (
                                <Typography.Text type="secondary">—</Typography.Text>
                            )
                    }

                    if (column.dataIndex === 'response') {
                        column.render = (raw: string | null, record: any) => (
                            <ResponseCell raw={raw} isError={rawOf(record)['status'] === 'error'} />
                        )
                    }
                },
            },
        },
        /** Меню дій рядка замість стандартних «Переглянути / Редагувати / Видалити» */
        storages: {
            tableAddActionsMenuDataSourceItemTransformer: {
                menuItems:
                    (crudContext: any) =>
                    ({ item }: { item: any }) => {
                        const raw = rawOf(item)
                        const items: any[] = []
                        if (raw['request']) {
                            items.push({
                                key: 'request',
                                icon: <MdOutlineFileOpen style={{ fontSize: 18 }} />,
                                label: 'Переглянути запит',
                                onClick: () => openRequest(crudContext, raw),
                            })
                        }
                        if (canProcess(raw)) {
                            items.push({
                                key: 'process',
                                icon: <MdOutlinePlayCircle style={{ fontSize: 18 }} />,
                                label: 'Запустити обробку',
                                onClick: () => void processImport(crudContext, raw),
                            })
                        }
                        return items
                    },
            },
        },
    },
    filter: {
        includeFields: ['id', 'status', 'type', 'entityType', 'entityId', 'createdAt'],
    },
    headerPage: {
        eventHandlers: {
            onButtons: {
                ReloadButton: ({ buttons, crudContext }: any) => {
                    buttons['ReloadButton'] = {
                        sort: 90,
                        priority: 'button',
                        // Та сама кнопка, що у звітах і на головній: іконка крутиться, поки вантажиться список
                        children: <ReloadButton crudContext={crudContext} />,
                    }
                },
            },
        },
    },
    settings: {
        searchFields: ['id', 'entityType', 'entityId'],
    },
}
